import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchStatusDashboardOverview,
  type StatusDashboardOverviewResponse,
} from '../services/statusDashboardService';

const REFRESH_INTERVAL_MS = 60_000;

interface StatusDashboardOverviewContextValue {
  data: StatusDashboardOverviewResponse | null;
  loading: boolean;
  error: string | null;
  fromCache: boolean;
  refetch: () => Promise<void>;
}

const StatusDashboardOverviewContext = createContext<StatusDashboardOverviewContextValue | undefined>(
  undefined,
);

export const useStatusDashboardOverview = (): StatusDashboardOverviewContextValue => {
  const context = useContext(StatusDashboardOverviewContext);
  if (!context) {
    throw new Error('useStatusDashboardOverview must be used within StatusDashboardOverviewProvider');
  }
  return context;
};

interface StatusDashboardOverviewProviderProps {
  children: ReactNode;
}

/**
 * Centraliza o fetch de /status/dashboard/resumo para evitar requisições duplicadas
 * entre o layout (banner de alerta) e a página StatusDashboard.
 */
export const StatusDashboardOverviewProvider: React.FC<StatusDashboardOverviewProviderProps> = ({
  children,
}) => {
  const [data, setData] = useState<StatusDashboardOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const isMountedRef = useRef(true);

  const loadOverview = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }

    try {
      const resp = await fetchStatusDashboardOverview();
      if (!isMountedRef.current) return;

      if (resp.success) {
        setData(resp);
        setFromCache(resp.fromCache === true);
        setError(null);
      } else {
        setError(resp.message || 'Erro ao buscar resumo do dashboard');
      }
    } catch {
      if (!isMountedRef.current) return;
      setError('Erro ao buscar resumo do dashboard');
    } finally {
      if (isMountedRef.current && !options?.silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    loadOverview();

    const refreshTimer = window.setInterval(() => {
      loadOverview({ silent: true });
    }, REFRESH_INTERVAL_MS);

    return () => {
      isMountedRef.current = false;
      window.clearInterval(refreshTimer);
    };
  }, [loadOverview]);

  const refetch = useCallback(async () => {
    await loadOverview();
  }, [loadOverview]);

  return (
    <StatusDashboardOverviewContext.Provider value={{ data, loading, error, fromCache, refetch }}>
      {children}
    </StatusDashboardOverviewContext.Provider>
  );
};
